import React, { useEffect, useState, useMemo } from "react"
import { useShallow } from "zustand/react/shallow"
import { useSearchParams } from "react-router-dom"

// components
import RichTextEditor from "../RichTextEditor/RichTextEditor"
import { PrimeButton, Select } from "components-design-system"

// hooks
import { useGetCommentTypes } from "api/hooks/admin/useCommentTypesApi"
import { usePostAuthoringComment, usePutAuthoringComment } from "api/hooks/studies/useAuthoringApi"
import { useGetAuthoringReviewMetadata } from "api/hooks/studies/useAuthoringReviewApi"
import useAuthoringViewStore from "../../../hooks/stores/useAuthoringViewStore"
import useAuthoringStore from "../../../hooks/stores/useAuthoringStore"

import "./RJCommentForm.scss"

const RJCommentForm = ({ jpath, label, editComment, setEditComment, setOpenForm, setShowMenu }) => {
  const { data: commentTypes = [] } = useGetCommentTypes()
  const [loadingPost, setLoadingPost] = useState(false)
  const [searchParams] = useSearchParams()
  const authoringId = useAuthoringStore((state) => state.authoringId)
  const [loading, setLoading] = React.useState(false)

  const { isReviewerView, formView, formSection } = useAuthoringViewStore(
    useShallow((state) => ({
      isReviewerView: state.isReviewerView,
      formView: state.formView,
      formSection: state.formSection,
    }))
  )

  const postAuthoringComment = usePostAuthoringComment(authoringId)
  const putAuthoringComment = usePutAuthoringComment(authoringId)

  const [comment, setComment] = useState("")
  const [commentType, setCommentType] = useState("Review")

  const [isDisabled, setIsDisabled] = useState(false)
  const projId = useMemo(() => {
    return searchParams.get("proj-id")
  }, [])

  const taskId = useMemo(() => {
    return searchParams.get("task-id")
  }, [])
  const { data: authReviewData } = useGetAuthoringReviewMetadata(
    isReviewerView === true ? authoringId : null,
    isReviewerView === true ? { "project-id": projId, "actionitem-id": taskId } : {}
  )

  useEffect(() => {
    if (editComment) {
      setComment(editComment.text)
      if (editComment.tags && editComment.tags[0]) {
        let type = editComment.tags[0]
        setCommentType(type)
      }
    } else {
      setComment("")
    }
  }, [editComment])

  useEffect(() => {
    if (!commentType || comment.length === 0 || comment === "<p><br></p>") {
      setIsDisabled(true)
    } else if (commentType && comment.length > 0 && isDisabled === true) {
      setIsDisabled(false)
    }
  }, [commentType, comment])

  const onPost = async () => {
    setLoadingPost(true)
    let newComment = {
      tags: [commentType],
      field_key: jpath,
      field_name: label,
      text: comment,
      resolved: false,
      favorite: false,
      form_view: formView?.id === "global" ? null : formView?.id,
      form_section: formSection?.id,
    }

    postAuthoringComment(
      { body: newComment },
      {
        onSuccess: () => {
          setComment("")
        },
        onSettled: () => {
          setLoadingPost(false)
        },
      }
    )
  }

  const onClose = () => {
    setOpenForm(false)
    setShowMenu(false)
  }

  const onSaveChanges = () => {
    setLoading(true)
    let updatedComment = {
      ...editComment,
      text: comment,
      tags: [commentType],
    }

    putAuthoringComment(
      { id: updatedComment.id + "/", body: updatedComment },
      {
        onSuccess: () => {
          setComment("")
          setEditComment(null)
        },
        onSettled: () => {
          setLoading(false)
        },
      }
    )
  }

  const commentOptions = useMemo(() => {
    if (commentTypes) {
      return commentTypes
        ?.filter((type) => {
          if (isReviewerView) {
            let foundIndex = authReviewData?.comment_types?.findIndex((id) => type.id === id)
            if (foundIndex !== -1) return true
            return false
          } else {
            return true
          }
        })
        ?.map((type) => type.name)
    }
    return []
  }, [isReviewerView, commentTypes])

  return (
    <div className="comment-container">
      <div
        id="authoring-comment-type-field"
        className="comment-type-dropdown"
      >
        Comment type: &nbsp;
        <div style={{ width: 200 }}>
          <Select
            options={commentOptions}
            popupContainerId={"authoring-comment-type-field"}
            value={commentType}
            onChange={(e) => setCommentType(e)}
          />
        </div>
      </div>
      <div style={{ width: "100%", height: 200 }}>
        <RichTextEditor
          id={"rich-text-editor-without-value"}
          value={comment}
          style={{ borderRadius: 5 }}
          setValue={setComment}
          isReviewerView={isReviewerView}
        />
      </div>
      {editComment ? (
        <div className="comment-action-container">
          <PrimeButton onClick={() => setEditComment(null)}>Cancel</PrimeButton>
          <PrimeButton
            type="primary"
            loading={loading}
            disabled={isDisabled}
            onClick={() => onSaveChanges()}
          >
            Save Changes
          </PrimeButton>
        </div>
      ) : (
        <div className="comment-action-container">
          <PrimeButton onClick={() => onClose()}>Close</PrimeButton>
          <PrimeButton
            type="primary"
            disabled={isDisabled}
            onClick={() => onPost()}
            loading={loadingPost}
          >
            Post
          </PrimeButton>
        </div>
      )}
    </div>
  )
}

export default RJCommentForm
